import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from "./axios";
import {
  GetBalanceAmountReportRequestT,
  GetPaymentReportRequestT,
  GetPurchaseEntryRequestT,
  GetPurchaseReturnReportRequestT,
  GetSalesReportRequestT,
  GetSalesReportResponseT,
  PurchaseReportEntryResponseT,
  SalesReturnReportRequestT,
  SalesReturnResponseT,
  GetBalanceAmountReportResponseT,
  GetInvoiceBalanceAmountReportResponseT
} from "../types/reportTypes";

export const useInfiniteGetSalesReturnReport = (
  postData: SalesReturnReportRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetSalesReturnReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post("/reports/sales-return", {
        ...postData,
        pageNo: pageParam,
      });
      return data as SalesReturnResponseT[] & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useInfiniteGetSalesReport = (postData: GetSalesReportRequestT) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetSalesReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post("/reports/sales-report", {
        ...postData,
        pageNo: pageParam,
      });
      return data as any & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useGetSalesReport = (postData: GetSalesReportRequestT) => {
  return useQuery({
    queryKey: ["useGetSalesReport", postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post("/reports/sales-report", {
        ...postData,
      });
      return data as GetSalesReportResponseT;
    },
  });
};

export const useInfiniteGetPurchaseReturnReport = (
  postData: GetPurchaseReturnReportRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetPurchaseReturnReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post("/reports/purchase-return", {
        ...postData,
        pageNo: pageParam,
      });
      return data as any & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useInfiniteGetPurchaseEntryReport = (
  postData: GetPurchaseEntryRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetPurchaseEntryReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post("/reports/purchase-entry", {
        ...postData,
        pageNo: pageParam,
      });
      return data as PurchaseReportEntryResponseT[] & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length : undefined;
      return nextPage;
    },
    initialPageParam: 0, // Start from page 0
  });
};

export const useInfiniteGetPaymentReport = (
  postData: GetPaymentReportRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetPaymentReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post("/reports/paymentReport", {
        ...postData,
        pageNo: pageParam,
      });
      return data as any & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useInfiniteGetBalanceAmountReport = (
  postData: GetBalanceAmountReportRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetBalanceAmountReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.post(
        "/reports/BalanceAmountReport",
        {
          ...postData,
          pageNo: pageParam,
        },
      );
      return data as GetBalanceAmountReportResponseT & {
        hasNextPage?: boolean;
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useInfiniteGetInvoiceBalanceAmountReport = (
  postData: GetBalanceAmountReportRequestT,
) => {
  return useInfiniteQuery({
    queryKey: ["useInfiniteGetInvoiceBalanceAmountReport", postData],
    queryFn: async ({ pageParam = 0 }) => {
      const { startDate, endDate, ...restData } = postData;
      const { data } = await axiosInstance.post(
        "/reports/balance-amount/invoices",
        {
          ...restData,
          fromDate: startDate,
          toDate: endDate,
          pageNo: pageParam,
        },
      );
      return data as GetInvoiceBalanceAmountReportResponseT & {
        hasNextPage?: boolean;
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length + 1 : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useDownloadPurchaseReport = () => {
  return useMutation({
    mutationFn: async (postData: GetPurchaseEntryRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/purchase-entry/export/excel"
          : "/reports/purchase-entry";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `purchase-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadSalesReport = () => {
  return useMutation({
    mutationFn: async (postData: GetSalesReportRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/sales-report/export/excel"
          : "/reports/sales-report";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadSalesReturnReport = () => {
  return useMutation({
    mutationFn: async (postData: SalesReturnReportRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/sales-return/export/excel"
          : "/reports/sales-return";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales-return-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadPurchaseReturnReport = () => {
  return useMutation({
    mutationFn: async (postData: GetPurchaseReturnReportRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/purchase-return/export/excel"
          : "/reports/purchase-return";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `purchase-return-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadPaymentReport = () => {
  return useMutation({
    mutationFn: async (postData: GetPaymentReportRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/payment-report/export/excel"
          : "/reports/paymentReport";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payment-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadBalanceAmountReport = () => {
  return useMutation({
    mutationFn: async (postData: GetBalanceAmountReportRequestT) => {
      const url =
        postData.docType === "EXCEL"
          ? "/reports/balance-amount/export/excel"
          : "/reports/BalanceAmountReport";
      const response = await axiosInstance.post(url, postData, {
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `balance-amount-report-${variables.startDate}-${variables.endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export type PLReportParams = {
  fromDate: string;
  toDate: string;
  branchId: number;
  isPrint: boolean;
};

export type PLReportResponse = {
  fromDate: string;
  toDate: string;
  grossSalesAmount: number;
  salesReturnAmount: number;
  netRevenue: number;
  salesTax: number;
  salesReturnTax: number;
  netTax: number;
  costOfGoodsSold: number;
  grossProfit: number;
  cashExpenses: number;
  bankExpenses: number;
  totalExpenses: number;
  netProfit: number;
  totalSalesDiscount: number;
  totalReturnDiscount: number;
  totalInvoices: number;
  totalReturns: number;
};

export const useGetPLReport = (params: PLReportParams, enabled: boolean) => {
  return useQuery({
    queryKey: ["useGetPLReport", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/daily-reports/pl-report", {
        params,
      });
      return data as PLReportResponse;
    },
    enabled,
  });
};

export const useDownloadPLReport = () => {
  return useMutation({
    mutationFn: async (params: PLReportParams & { isExcel?: boolean }) => {
      const url = params.isExcel
        ? "/daily-reports/pl-report/export/excel"
        : "/daily-reports/pl-report";
      // Exclude isExcel from params sent to backend
      const { isExcel, ...restParams } = params;
      const response = await axiosInstance.get(url, {
        params: isExcel ? restParams : { ...restParams, isPrint: true },
        responseType: "blob",
      });
      return response.data as Blob;
    },
    onSuccess: (blob, variables) => {
      const ext = variables.isExcel ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pl-report-${variables.fromDate}-${variables.toDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export type StaffPerformanceParams = {
  branchId: number;
  startDate: string;
  endDate: string;
  page: number;
  size: number;
};

export type StaffPerformanceItem = {
  staffId: number;
  employeeName: string;
  totalSales: number;
  totalInvoice: number;
  avgTicket: number;
  profitGenerated: number;
};

export type StaffPerformanceResponse = {
  content: StaffPerformanceItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isLast: boolean;
};

export const useGetStaffPerformanceReport = (
  params: StaffPerformanceParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["useGetStaffPerformanceReport", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        "/daily-reports/staff-sales-performance",
        { params },
      );
      return data as StaffPerformanceResponse;
    },
    enabled,
  });
};

export type GraphItem = {
  month: string | null;
  dayName: string | null;
  year: string | null;
  weekStart: string; // ISO date string like "2025-07-28"
  netProfit: number;
  revenue: number;
  cashFlow: number;
};

export const useGetChartDataQuery = (
  interval: "WEEKLY" | "MONTHLY" | "YEARLY",
) => {
  return useQuery({
    queryKey: ["chart-data", interval],
    queryFn: async () => {
      const response = await axiosInstance.post("/reports/getChartData", {
        interval,
      });
      return response.data as GraphItem[];
    },
  });
};

// ── Batch Profit Report ───────────────────────────────────────────────────────

export type BatchProfitRequestParams = {
  pageNo: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  branchId?: number;
  customerId?: number;
  productId?: number;
  invoiceNumber?: string;
  purchaseNumber?: string;
};

export type BatchProfitItem = {
  salesNumber: string;
  salesDate: string;
  customerName: string;
  productId: number;
  productName: string;
  purchaseNumber: string;
  purchaseDate: string;
  soldQuantityFromBatch: number;
  returnedQuantityFromBatch: number;
  netSoldQuantityFromBatch: number;
  purchaseUnitCost: number;
  netSaleUnitPrice: number;
  profitPerUnit: number;
  totalProfit: number;
};

export type BatchProfitResponse = {
  content: BatchProfitItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
};

export const useGetBatchProfitReport = (
  params: BatchProfitRequestParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["useGetBatchProfitReport", params],
    queryFn: async () => {
      const { data } = await axiosInstance.post(
        "/reports/batch-profit",
        params,
      );
      return data as BatchProfitResponse;
    },
    enabled,
  });
};

export const useDownloadBatchProfitReportExcel = () => {
  return useMutation({
    mutationFn: async (postData: BatchProfitRequestParams) => {
      const response = await axiosInstance.post(
        "/reports/batch-profit/export/excel",
        postData,
        { responseType: "blob" },
      );
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `batch-profit-${variables.startDate}-${variables.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadConsolidateReportExcel = () => {
  return useMutation({
    mutationFn: async (postData: { startDate: string; endDate: string }) => {
      const response = await axiosInstance.post(
        "/reports/consolidate-report/export/excel",
        postData,
        { responseType: "blob" },
      );
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `consolidated-totals-${variables.startDate}-${variables.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadSalesAndReturnsExcel = () => {
  return useMutation({
    mutationFn: async (postData: any) => {
      const response = await axiosInstance.post(
        "/reports/sales-and-returns/export/excel",
        postData,
        { responseType: "blob" },
      );
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales-and-returns-${variables.startDate}-${variables.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadStaffSalesPerformanceExcel = () => {
  return useMutation({
    mutationFn: async (params: StaffPerformanceParams) => {
      const response = await axiosInstance.get(
        "/daily-reports/staff-sales-performance/export/excel",
        { params, responseType: "blob" },
      );
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `staff-sales-performance-${variables.startDate}-${variables.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

// ── Write-Offs Report ────────────────────────────────────────────────────────

export type WriteOffsReportParams = {
  type?: string;
  branchId?: number;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
};

export type WriteOffItem = {
  id: number;
  writeOffNumber: string;
  type: string;
  status: string;
  writeOffDate: string;
  amount: number;
  quantity: number;
  unit: string;
  unitCost: number;
  reason: string;
  notes: string;
  salesId: number;
  invoiceNumber: string;
  customerId: number;
  customerName: string;
  damagedProductId: number;
  branchId: number;
  createdBy: string;
};

export type WriteOffsReportResponse = {
  content: WriteOffItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isLast: boolean;
};

export const useGetWriteOffsReport = (
  params: WriteOffsReportParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["useGetWriteOffsReport", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/write-offs", { params });
      return data as WriteOffsReportResponse;
    },
    enabled,
  });
};

export const useDownloadWriteOffsReportExcel = () => {
  return useMutation({
    mutationFn: async (params: WriteOffsReportParams) => {
      const response = await axiosInstance.get("/write-offs", {
        params: { ...params, isDownload: true },
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `write-offs-report${
        variables.fromDate ? `-${variables.fromDate}` : ""
      }${variables.toDate ? `-${variables.toDate}` : ""}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export type WriteOffCreditBillsPayload = {
  writeOffDate: string;
  reason: string;
  notes: string;
  items: {
    salesId: number;
    amount: number;
  }[];
};

export const useWriteOffCreditBills = () => {
  return useMutation({
    mutationFn: async (payload: WriteOffCreditBillsPayload) => {
      const { data } = await axiosInstance.post("/write-offs/credit-bills", payload);
      return data;
    },
  });
};

export type WriteOffPurchaseCreditBillsPayload = {
  writeOffDate: string;
  reason: string;
  notes: string;
  items: {
    purchaseEntryId: number;
    amount: number;
  }[];
};

export const useWriteOffPurchaseCreditBills = () => {
  return useMutation({
    mutationFn: async (payload: WriteOffPurchaseCreditBillsPayload) => {
      const { data } = await axiosInstance.post("/write-offs/purchase-credit-bills", payload);
      return data;
    },
  });
};
